import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import { useDownloadLeadsMutation } from 'services/api/leads.api';
import type { ExtendedLead } from 'services/types/lead';
import { BRAVOAUTO_BUSINESS_UNIT_ID, INSUREX_BUSINESS_UNIT_ID, KOVACS_BUSINESS_UNIT_ID } from 'utils/constants';
import { getPatenteFromWebhookBody, translateLeadStatus } from 'utils/leadTableColumns';
import removeCSVInjection from 'utils/removeCSVInjection';

interface DownloadLeadsDialogProps {
  open: boolean;
  onClose: () => void;
  businessUnitId: number;
  filterModel: string;
  sortModel: string;
  totalLeads: number;
}

const SPACE_USAGE_BYTES_PER_LEAD_BASIC = 200; // ~200b / lead
const SPACE_USAGE_BYTES_PER_LEAD_FULL = 3000; // ~3kb / lead
const ESTIMATED_SECONDS_PER_REQUEST_BASIC = 0.5; // Approximate rate of processing
const ESTIMATED_SECONDS_PER_REQUEST_FULL = 4; // Approximate rate of processing
const PAGE_SIZE = 500;

const MUEVE_SEGURO_UTM_HEADERS = [
  'UTM Source',
  'UTM Medium',
  'UTM Campaign',
  'UTM ID',
  'UTM Term',
  'UTM Content',
  'UTM URL Origin',
] as const;

const MUEVE_SEGURO_UTM_FIELDS: Array<keyof NonNullable<ExtendedLead['utmFromWebhook']>> = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmId',
  'utmTerm',
  'utmContent',
  'utmUrlOrigin',
];

const BUSINESS_UNIT_HEADERS: Record<string, string[]> = {
  car_dealership: ['Marca inicial', 'Modelo inicial', 'Concesionario', 'Sucursal', 'Propietario externo'],
  financial_institution: ['En Dicom', 'Ingresos', 'Pago mensual deudas', 'Carga financiera'],
  insurance_broker: [],
};

/**
 * Helper function to extract UTM values from a lead
 */
const getMueveSeguroUTMValues = (lead: ExtendedLead): string[] => {
  return MUEVE_SEGURO_UTM_FIELDS.map((field) => lead.utmFromWebhook?.[field] || '');
};

const formatBytes = (bytes: number) => {
  const mb = bytes / (1024 * 1024);
  if (mb < 0.1) {
    return `${mb.toFixed(2)} MB`;
  }
  if (mb < 10) {
    return `${mb.toFixed(1)} MB`;
  }
  return `${Math.round(mb)} MB`;
};

const formatTime = (seconds: number) => {
  if (seconds < 60) return `${Math.round(seconds)} seg`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
};

const DownloadLeadsDialog: React.FC<DownloadLeadsDialogProps> = ({
  open,
  onClose,
  businessUnitId,
  filterModel,
  sortModel,
  totalLeads,
}) => {
  const [fullDownload, setFullDownload] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadLeads] = useDownloadLeadsMutation();

  const expectedRequests = Math.ceil(totalLeads / PAGE_SIZE);

  const sizeBytesBasic = totalLeads * SPACE_USAGE_BYTES_PER_LEAD_BASIC;
  const sizeBytesFull = totalLeads * SPACE_USAGE_BYTES_PER_LEAD_FULL;
  const secondsBasic = expectedRequests * ESTIMATED_SECONDS_PER_REQUEST_BASIC;
  const secondsFull = expectedRequests * ESTIMATED_SECONDS_PER_REQUEST_FULL;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setProgress(0);
      let page = 0;
      let hasMore = true;
      const allLeads: ExtendedLead[] = [];

      while (hasMore) {
        const response = await downloadLeads({
          businessUnitId,
          filterModel,
          paginationModel: JSON.stringify({ page, pageSize: PAGE_SIZE }),
          includeFullDetails: fullDownload,
          sortModel,
        }).unwrap();

        allLeads.push(...response.leads);
        hasMore = response.leads.length === PAGE_SIZE;
        page += 1;
        setProgress((page / expectedRequests) * 100);
      }

      // Generate CSV headers based on business unit type
      const getHeadersForBusinessUnit = (
        businessUnitType?: string,
        includeCreditColumns = false,
        includeMueveSeguroUTM = false,
        includeKovacsAIComment = false,
        includePatente = false
      ) => {
        const baseHeaders = [
          'ID',
          'Unidad Negocio',
          'ID Cliente',
          'Link',
          'Estado',
          'Nombre',
          'Apellido',
          'Teléfono',
          'Email',
          'RUT',
          'Origen',
          'Campaña',
          'url origen',
          'UTM source',
          'Campaña Meta',
          'Anuncio Meta',
          'Actualización CRM',
          'Creación',
          'Mensajes',
          'Mensajes del cliente',
          'Total mensajes (histórico)',
          'Total mensajes del cliente (histórico)',
          'Total de mensajes fallidos',
          'Fecha de visita',
          'Descartado',
          'ID Externo',
          'Respuestas Automáticas Deshabilitadas',
          'Follow Ups/Campañas Deshabilitadas',
        ];

        const kovacsAICommentHeaders = includeKovacsAIComment ? ['Comentario AI'] : [];

        const patenteHeaders = includePatente ? ['Patente'] : [];

        const businessUnitSpecificHeaders = businessUnitType
          ? [...(BUSINESS_UNIT_HEADERS[businessUnitType] || [])]
          : [];

        if (businessUnitType === 'car_dealership' && includeCreditColumns) {
          businessUnitSpecificHeaders.push('Estado Crédito', 'Número de solicitud en Santander');
        }

        // Add Mueve Seguro specific columns (UTM, topics, and endReason)
        const mueveSeguroHeaders = includeMueveSeguroUTM
          ? [...MUEVE_SEGURO_UTM_HEADERS, 'Tópicos', 'Motivo de término']
          : [];

        const fullDownloadHeaders = fullDownload
          ? [
              'Usuario asignado',
              ...(includeMueveSeguroUTM ? [] : ['Motivo de término']), // Exclude from full download if already in Mueve Seguro columns
              'Payload CRM',
              'Respuesta CRM',
              'Información inicial',
            ]
          : [];

        return [
          ...baseHeaders,
          ...kovacsAICommentHeaders,
          ...patenteHeaders,
          ...businessUnitSpecificHeaders,
          ...mueveSeguroHeaders,
          ...fullDownloadHeaders,
        ];
      };

      // Get business unit type from the first lead (assuming all leads are from the same business unit)
      // If no leads or mixed business units, use the current behavior (no specific fields)
      const detectedBusinessUnitType = allLeads.length > 0 ? allLeads[0]?.client?.businessUnit?.type : undefined;

      // Check if all leads are from the same business unit
      const allSameBusinessUnit = allLeads.every(
        (lead) => lead.client?.businessUnit?.type === detectedBusinessUnitType
      );

      const allMueveSeguroLeads =
        businessUnitId === INSUREX_BUSINESS_UNIT_ID ||
        (allLeads.length > 0 && !allLeads.some((lead) => lead.client?.businessUnit?.id !== INSUREX_BUSINESS_UNIT_ID));

      const effectiveBusinessUnitType = allSameBusinessUnit ? detectedBusinessUnitType : undefined;
      const AUTOLAND_VENTAS_BUSINESS_UNIT_ID = 45;
      const includeCreditColumns = businessUnitId === AUTOLAND_VENTAS_BUSINESS_UNIT_ID;
      const includeKovacsAIComment = businessUnitId === KOVACS_BUSINESS_UNIT_ID;
      const includePatente = businessUnitId === BRAVOAUTO_BUSINESS_UNIT_ID;
      const headers = getHeadersForBusinessUnit(
        effectiveBusinessUnitType,
        includeCreditColumns,
        allMueveSeguroLeads,
        includeKovacsAIComment,
        includePatente
      );

      const getValuesForLead = (
        lead: ExtendedLead,
        businessUnitType: string | undefined = undefined,
        shouldIncludeCreditColumns = false,
        shouldIncludeMueveSeguroUTM = false,
        shouldIncludeKovacsAIComment = false,
        shouldIncludePatente = false
      ) => {
        const baseValues = [
          lead.id,
          lead.client?.businessUnit?.name,
          lead.clientId,
          `${window.location.origin}/clients/${lead.client?.id}`,
          translateLeadStatus(lead.status),
          lead.client?.firstName,
          lead.client?.lastName,
          lead.client?.phone,
          lead.client?.email,
          lead.client?.rut || '',
          lead.source,
          lead.outboundCampaignId,
          lead.url,
          lead.UTM?.source,
          lead.metaAd?.metaAdSet?.metaCampaign?.name || '',
          lead.metaAd?.name || '',
          lead.CRMUpdateAt ? new Date(lead.CRMUpdateAt).toLocaleString('es-CL', { hour12: false }) : '',
          new Date(lead.createdAt).toLocaleString('es-CL', { hour12: false }),
          lead.messagesCount,
          lead.clientMessagesCount,
          lead.client?.messagesCount,
          lead.client?.clientMessagesCount,
          lead.client?.failedMessagesCount,
          lead.visitDate ? new Date(lead.visitDate).toLocaleString('es-CL', { hour12: false }) : '',
          lead.discarded,
          lead.externalId || '',
          lead.client?.disabledAutomaticResponses ? 'Sí' : 'No',
          lead.client?.disabledFollowUps ? 'Sí' : 'No',
        ];

        const kovacsAICommentValues = shouldIncludeKovacsAIComment ? [lead.AIComment ?? ''] : [];

        // The plate comes from an external API, so neutralize formula prefixes before it lands in a cell
        const patenteValues = shouldIncludePatente ? [removeCSVInjection(getPatenteFromWebhookBody(lead))] : [];

        const businessUnitSpecificValues = [];

        if (businessUnitType === 'car_dealership') {
          businessUnitSpecificValues.push(
            lead.initialBrandOfInterest || '',
            lead.initialModelOfInterest || '',
            lead.dealership || '',
            lead.branchOffice || '',
            lead.externalLeadOwner || ''
          );
          if (shouldIncludeCreditColumns) {
            const creditStatus = (lead.client?.creditStatus as string) || '';
            const simulations = lead.client?.creditSimulations;
            let creditSimulationRequestId = '';
            if (simulations && Array.isArray(simulations) && simulations.length > 0) {
              const lastElement = simulations[simulations.length - 1];
              // Handle nested array structure: [[{requestId: "..."}], ...]
              if (Array.isArray(lastElement) && lastElement.length > 0) {
                const lastSimulation = lastElement[lastElement.length - 1] as { requestId?: string } | undefined;
                creditSimulationRequestId = lastSimulation?.requestId || '';
              } else if (lastElement && typeof lastElement === 'object' && 'requestId' in lastElement) {
                // Handle flat array structure: [{requestId: "..."}, ...]
                creditSimulationRequestId = (lastElement as { requestId?: string }).requestId || '';
              }
            }
            businessUnitSpecificValues.push(creditStatus, creditSimulationRequestId);
          }
        } else if (businessUnitType === 'financial_institution') {
          const financialLoadRange = lead.client?.customAttributes?.financialLoadRange;
          const financialLoadTranslation = (() => {
            if (financialLoadRange === 'low') return 'Baja';
            if (financialLoadRange === 'medium') return 'Media';
            if (financialLoadRange === 'high') return 'Alta';
            return financialLoadRange || '';
          })();

          businessUnitSpecificValues.push(
            lead.client?.customAttributes?.isInDicom ? 'Sí' : 'No',
            lead.client?.customAttributes?.income
              ? `$${lead.client.customAttributes.income.toLocaleString('es-CL')}`
              : '',
            lead.client?.customAttributes?.debtsMonthlyPayment
              ? `$${lead.client.customAttributes.debtsMonthlyPayment.toLocaleString('es-CL')}`
              : '',
            financialLoadTranslation
          );
        }

        // Add Mueve Seguro specific values (UTM, topics, and endReason)
        const mueveSeguroValues = shouldIncludeMueveSeguroUTM
          ? [...getMueveSeguroUTMValues(lead), lead.topics?.join(', ') || '', lead.endReason || '']
          : [];

        const fullDownloadValues = fullDownload
          ? [
              lead.client?.user?.name || '',
              ...(shouldIncludeMueveSeguroUTM ? [] : [lead.endReason || '']), // Exclude from full download if already in Mueve Seguro columns
              lead.CRMCreationPayload ? JSON.stringify(lead.CRMCreationPayload) : '',
              lead.CRMCreationResponse ? JSON.stringify(lead.CRMCreationResponse) : '',
              lead.webhookBodyAtCreation ? JSON.stringify(lead.webhookBodyAtCreation) : '',
            ]
          : [];

        return [
          ...baseValues,
          ...kovacsAICommentValues,
          ...patenteValues,
          ...businessUnitSpecificValues,
          ...mueveSeguroValues,
          ...fullDownloadValues,
        ];
      };

      const rows = allLeads.map((lead) => {
        const values = getValuesForLead(
          lead,
          effectiveBusinessUnitType,
          includeCreditColumns,
          allMueveSeguroLeads,
          includeKovacsAIComment,
          includePatente
        );

        return values
          .map((value) => {
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(';') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join(';');
      });

      const csvContent = [headers.join(';'), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onClose();
    } catch (error) {
      console.error('Error downloading leads:', error);
    } finally {
      setDownloading(false);
      setProgress(0);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Descargar leads</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Typography>
            Se descargará un archivo CSV (compatible con excel) tomando todos los prospectos con los filtros aplicados
            (Fechas, Campaña, Unidad de negocio)
          </Typography>
          <Typography
            sx={{
              mb: 2,
              mt: 1,
            }}
          >
            <strong>Total de leads: </strong>
            {totalLeads}
          </Typography>
          <FormControl>
            <RadioGroup
              value={fullDownload ? 'full' : 'basic'}
              onChange={(e) => setFullDownload(e.target.value === 'full')}
            >
              <FormControlLabel
                value="basic"
                control={<Radio />}
                label={`Información básica (~${formatBytes(sizeBytesBasic)}, ~${formatTime(secondsBasic)})`}
              />
              <FormControlLabel
                value="full"
                control={<Radio />}
                label={`Información completa (~${formatBytes(sizeBytesFull)}, ~${formatTime(secondsFull)})`}
              />
            </RadioGroup>
          </FormControl>
          {downloading && (
            <Box
              sx={{
                mt: 2,
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  mb: 1,
                }}
              >
                Procesando... {Math.round(progress)}%
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={() => {
            handleDownload().catch(console.error);
          }}
          variant="contained"
          disabled={downloading}
        >
          {downloading ? 'Descargando...' : 'Descargar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadLeadsDialog;
